import { toClientEvmSigner, type ChannelConfig } from "@x402/evm";
import {
  BatchSettlementEvmScheme,
  buildChannelConfig,
  computeChannelId,
  createBatchSettlementEIP3009DepositPayload,
  processSettleResponse,
  readChannelBalanceAndTotalClaimed,
} from "@x402/evm/batch-settlement/client";
import { ExactEvmScheme } from "@x402/evm/exact/client";
import {
  decodePaymentRequiredHeader,
  encodePaymentSignatureHeader,
} from "@x402/core/http";
import type {
  PaymentPayload,
  PaymentRequirements,
} from "@x402/core/types";
import {
  decodePaymentResponseHeader,
  wrapFetchWithPayment,
  x402Client,
} from "@x402/fetch";
import {
  createPublicClient,
  http,
  type EIP1193Provider,
} from "viem";
import { baseSepolia } from "viem/chains";

import {
  assertExpectedPaymentChallenge,
  paymentFailureMessageFromHeader,
} from "./payment-challenge.js";
import {
  assertBatchChannelIdentity,
  batchChannelSalt,
  batchTopUpCumulativeAmount,
  BrowserBatchChannelStorage,
} from "./batch-channel-storage.js";
import { revealAccountResult } from "./account-result.js";
import {
  callProgressCopy,
  callProgressStages,
  type CallProgressStage,
} from "./call-progress.js";
import {
  configuredResponseBodyHTML,
  parseResponseDefinition,
  type ResponseDisplayDefinition,
} from "./response-display.js";
import { walletIdentityAfterSessionClear } from "./wallet-session.js";
import { normalizePaidCallResponse } from "./call-result.js";
import { createVerifiedBrowserWalletSigner } from "./browser-wallet-signer.js";
import "./style.css";

const BASE_SEPOLIA_NETWORK = "eip155:84532";
const BASE_SEPOLIA_USDC = "0x036CbD53842c5426634e7929541eC2318f3dCF7e";
const BASE_SEPOLIA_RPC = "https://sepolia.base.org";

type Product = {
  id: string;
  slug: string;
  name: string;
  description: string;
  productType?: "ON_DEMAND" | "NOTIFICATION";
  billingModel?: "PER_INVOCATION" | "PREPAID_ENTITLEMENT";
  notification?: {
    includedUsageCount: number;
    usageUnitAmountAtomic: string;
  } | null;
  method: string;
  providerType: string;
  providerAction?: string;
  requestEncoding: "JSON" | "QUERY" | "MULTIPART";
  requestSchema: string;
  requestTemplate: string;
  responseSchema: string;
  paymentMode: "EXACT" | "BATCH";
  supportedPaymentModes?: Array<"EXACT" | "BATCH">;
  network: string;
  tokenAddress: string;
  priceUsdcAtomic: string;
  receiverWallet: string;
  callPath: string;
  callPaths?: {
    exact: string;
    batch: string;
    batchTopUp: string;
  };
  version: number;
};

type ParameterDefinition = {
  name: string;
  label: string;
  type: "string" | "textarea" | "integer" | "number" | "boolean" | "select" | "chain" | "date" | "datetime" | "file" | "json" | "array";
  required: boolean;
  placeholder?: string;
  helpText?: string;
  defaultValue?: string;
  options?: Array<{ label: string; value: string }>;
  accept?: string;
  maxBytes?: number;
  format?: "email" | "uri";
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  items?: ParameterItemDefinition;
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
};

type ParameterItemDefinition = {
  type: "string" | "integer" | "number" | "boolean" | "select" | "chain" | "date" | "datetime";
  placeholder?: string;
  options?: Array<{ label: string; value: string }>;
  format?: "email" | "uri";
  minLength?: number;
  maxLength?: number;
  pattern?: string;
};

type Order = {
  id: string;
  status: string;
  paymentState: string;
  serviceState: string;
  transactionHash?: string;
};

type WalletOrder = {
  id: string;
  productId: string;
  productName: string;
  status: string;
  paymentState: string;
  serviceState: string;
  transactionHash?: string;
  responseHash?: string;
  errorCode?: string;
  network: string;
  tokenAddress: string;
  priceUsdcAtomic: string;
  createdAt: string;
  updatedAt: string;
};

type WalletOrderPage = {
  orders: WalletOrder[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  summary: {
    totalOrders: number;
    paidOrders?: number;
    settledOrders: number;
    totalSpentAtomic: string;
  };
};

type WalletBatchChannel = {
  channelId: string;
  channelConfig: ChannelConfig;
  productId: string;
  productName: string;
  productIds?: string[];
  productNames?: string[];
  network: string;
  token: string;
  receiver: string;
  balance: string;
  chargedCumulativeAmount: string;
  totalClaimed: string;
  signedMaxClaimable: string;
  signature: `0x${string}`;
  availableAmount: string;
  claimableAmount: string;
  priceUsdcAtomic: string;
  remainingCalls: string;
  withdrawRequestedAt: number;
  lastRequestTimestamp: number;
  refundRequest?: {
    id: string;
    requestedAmountAtomic: string;
    status: string;
    availableAt: string;
    ready: boolean;
  };
};

type WalletBatchFundEvent = {
  id: string;
  kind: "TOP_UP" | "REFUND";
  wallet: string;
  productId: string;
  productName: string;
  channelId: string;
  requestedAmountAtomic: string;
  actualAmountAtomic: string;
  status: string;
  availableAt?: string;
  transactionHash?: string;
  ready: boolean;
  createdAt: string;
  updatedAt: string;
};

type WalletNotificationSubscription = {
  id: string;
  productId: string;
  productName: string;
  orderId: string;
  status: string;
  quotaTotal: number;
  quotaRemaining: number;
  balanceTotalAtomic: string;
  balanceRemainingAtomic: string;
  terminationReason?: string;
  activationCallbackStatus?: string;
  terminationCallbackStatus?: string;
  activatedAt?: string;
  createdAt: string;
};

declare global {
  interface Window {
    ethereum?: EIP1193Provider;
  }
}

const apiBase = import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8080";
const providerAPIDocsURL = `${apiBase.replace(/\/+$/, "")}/provider-integration/docs`;
const portalRoute = window.location.pathname.replace(/\/+$/, "") || "/";
const isServiceCLIRoute = portalRoute === "/service-cli";

if (
  portalRoute === "/provider-integration/docs" &&
  window.location.href !== providerAPIDocsURL
) {
  window.location.replace(providerAPIDocsURL);
} else {
  document.querySelectorAll<HTMLAnchorElement>("[data-provider-api-link]").forEach(link => {
    link.href = providerAPIDocsURL;
  });
}

if (isServiceCLIRoute) {
  document.body.classList.add("cli-docs-route");
  document.querySelector<HTMLElement>("[data-marketplace-page]")!.hidden = true;
  document.querySelector<HTMLElement>("[data-cli-docs-page]")!.hidden = false;
  document.querySelector<HTMLAnchorElement>(".cli-doc-link")!.setAttribute("aria-current", "page");
  document.title = "Service CLI · AI 直接调用 · bizX402";
  document.querySelector<HTMLMetaElement>('meta[name="description"]')!.content =
    "安装 bizX402 CLI 与 MCP，让 AI 安全发现、报价并调用 x402 付费服务。";
}

document.querySelectorAll<HTMLButtonElement>("[data-copy-target]").forEach(button => {
  button.addEventListener("click", async () => {
    const target = document.querySelector<HTMLElement>(`#${button.dataset.copyTarget}`);
    if (!target) return;
    await navigator.clipboard.writeText(target.textContent || "");
    button.dataset.copied = "true";
    button.textContent = "已复制";
    window.setTimeout(() => {
      delete button.dataset.copied;
      button.textContent = "复制";
    }, 1600);
  });
});
let products: Product[] = [];
let selected: Product | undefined;
let selectedPaymentMode: "EXACT" | "BATCH" = "EXACT";
let provider: EIP1193Provider | undefined;
let walletAddress: `0x${string}` | undefined;
let callPending = false;
let callProgressStage: CallProgressStage = "quote";
let callProgressStartedAt = 0;
let callProgressTimer: number | undefined;
let accountToken = sessionStorage.getItem("bizx402-wallet-token") || "";
let accountWallet = sessionStorage.getItem("bizx402-wallet-address") || "";
let accountOrders: WalletOrder[] = [];
let accountChannels: WalletBatchChannel[] = [];
let accountFundEvents: WalletBatchFundEvent[] = [];
let accountSubscriptions: WalletNotificationSubscription[] = [];
let accountPage = 1;
let accountTotalPages = 0;
let batchTopUpPending = false;
let batchRefundPending = false;
let batchRefundHoldingSeconds = 300;

const productGrid = document.querySelector<HTMLElement>("#product-grid")!;
const catalogNote = document.querySelector<HTMLElement>("#catalog-note")!;
const orderForm = document.querySelector<HTMLFormElement>("#order-form")!;
const callButton = document.querySelector<HTMLButtonElement>("#create-order")!;
const requestCard = document.querySelector<HTMLFormElement>(".request-card")!;
const callProgress = document.querySelector<HTMLElement>("#call-progress")!;
const callProgressTitle = document.querySelector<HTMLElement>("#call-progress-title")!;
const callProgressDetail = document.querySelector<HTMLElement>("#call-progress-detail")!;
const callProgressElapsed = document.querySelector<HTMLElement>("#call-progress-elapsed")!;
const walletIdentity = document.querySelector<HTMLElement>("#wallet-identity")!;
const walletLabel = document.querySelector<HTMLElement>("#wallet-label")!;
const statusBox = document.querySelector<HTMLElement>("#status")!;
const orderResult = document.querySelector<HTMLElement>("#order-result")!;
const serviceResult = document.querySelector<HTMLElement>("#service-result")!;
const requestFields = document.querySelector<HTMLElement>("#request-fields")!;
const checkout = document.querySelector<HTMLElement>("#checkout")!;
const serviceModal = document.querySelector<HTMLDialogElement>("#service-modal")!;
const serviceModalPanel = document.querySelector<HTMLElement>(".service-modal-panel")!;
const closeServiceModalButton = document.querySelector<HTMLButtonElement>("#close-service-modal")!;
const accountButton = document.querySelector<HTMLButtonElement>("#account-button")!;
const accountLabel = document.querySelector<HTMLElement>("#account-label")!;
const accountModal = document.querySelector<HTMLDialogElement>("#account-modal")!;
const accountLogin = document.querySelector<HTMLElement>("#account-login")!;
const accountCenter = document.querySelector<HTMLElement>("#account-center")!;
const accountStatus = document.querySelector<HTMLElement>("#account-status")!;
const accountOrderRows = document.querySelector<HTMLElement>("#account-order-rows")!;
const accountResult = document.querySelector<HTMLElement>("#account-result")!;
const accountResultContent = document.querySelector<HTMLElement>("#account-result-content")!;
const accountResultProduct = document.querySelector<HTMLElement>("#account-result-product")!;
const accountResultOrder = document.querySelector<HTMLElement>("#account-result-order")!;
const accountResultStatus = document.querySelector<HTMLElement>("#account-result-status")!;
const accountResultPayment = document.querySelector<HTMLElement>("#account-result-payment")!;
const accountResultService = document.querySelector<HTMLElement>("#account-result-service")!;
const accountResultPrice = document.querySelector<HTMLElement>("#account-result-price")!;
const accountResultTime = document.querySelector<HTMLElement>("#account-result-time")!;
const accountChannelGrid = document.querySelector<HTMLElement>("#account-channel-grid")!;
const accountFundRows = document.querySelector<HTMLElement>("#account-fund-rows")!;
const accountSubscriptionRows = document.querySelector<HTMLElement>("#account-subscription-rows")!;
const batchTopup = document.querySelector<HTMLElement>("#batch-topup")!;
const batchDepositCalls = document.querySelector<HTMLInputElement>("#batch-deposit-calls")!;
const batchDepositTotal = document.querySelector<HTMLElement>("#batch-deposit-total")!;
const batchDepositNote = document.querySelector<HTMLElement>("#batch-deposit-note")!;
const batchChannelHeadline = document.querySelector<HTMLElement>("#batch-channel-headline")!;
const batchChannelDetail = document.querySelector<HTMLElement>("#batch-channel-detail")!;
const batchRechargeButton = document.querySelector<HTMLButtonElement>("#batch-recharge")!;
const batchRechargeStatus = document.querySelector<HTMLElement>("#batch-recharge-status")!;
const batchCallGate = document.querySelector<HTMLElement>("#batch-call-gate")!;
const batchCallHeadline = document.querySelector<HTMLElement>("#batch-call-headline")!;
const batchCallDetail = document.querySelector<HTMLElement>("#batch-call-detail")!;
const paymentSafetyNote = document.querySelector<HTMLElement>("#payment-safety-note")!;
const batchChannelStorage = new BrowserBatchChannelStorage(localStorage);

function escapeHTML(value: string): string {
  return value.replace(/[&<>"']/g, character => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[character]!);
}

function parseParameterDefinitions(raw: string): ParameterDefinition[] {
  try {
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function scalarControlHTML(
  definition: ParameterDefinition | ParameterItemDefinition,
  dataAttribute: string,
  value: unknown = "",
  required = false,
  arrayItem = false,
): string {
  const textValue = typeof value === "string" || typeof value === "number" ? String(value) : "";
  const placeholder = definition.placeholder ? ` placeholder="${escapeHTML(definition.placeholder)}"` : "";
  const requiredAttribute = required ? " required" : "";
  const stringConstraints = `${definition.minLength ? ` minlength="${definition.minLength}"` : ""}${definition.maxLength ? ` maxlength="${definition.maxLength}"` : ""}${definition.pattern ? ` pattern="${escapeHTML(definition.pattern)}"` : ""}`;
  if (definition.type === "textarea" || definition.type === "json") {
    return `<textarea ${dataAttribute} rows="4"${placeholder}${stringConstraints}${requiredAttribute}>${escapeHTML(textValue)}</textarea>`;
  }
  if (definition.type === "integer" || definition.type === "number") {
    return `<input ${dataAttribute} type="number" step="${definition.type === "integer" ? "1" : "any"}" value="${escapeHTML(textValue)}"${placeholder}${requiredAttribute} />`;
  }
  if (definition.type === "boolean") {
    if (arrayItem) {
      return `<select ${dataAttribute}><option value="">请选择</option><option value="true"${value === true || value === "true" ? " selected" : ""}>是</option><option value="false"${value === false || value === "false" ? " selected" : ""}>否</option></select>`;
    }
    return `<label class="runtime-check"><input ${dataAttribute} type="checkbox"${value === true || value === "true" ? " checked" : ""} /><span>启用</span></label>`;
  }
  if (definition.type === "select" || definition.type === "chain") {
    return `<select ${dataAttribute}${requiredAttribute}>${(definition.options || []).map(option => `<option value="${escapeHTML(option.value)}"${textValue === option.value ? " selected" : ""}>${escapeHTML(option.label)}</option>`).join("")}</select>`;
  }
  if (definition.type === "date" || definition.type === "datetime") {
    return `<input ${dataAttribute} type="${definition.type === "date" ? "date" : "datetime-local"}" value="${escapeHTML(textValue)}"${requiredAttribute} />`;
  }
  if (definition.type === "file") {
    const fileDefinition = definition as ParameterDefinition;
    return `<input ${dataAttribute} type="file"${fileDefinition.accept ? ` accept="${escapeHTML(fileDefinition.accept)}"` : ""}${requiredAttribute} />`;
  }
  const inputType = definition.format === "email" ? "email" : definition.format === "uri" ? "url" : "text";
  return `<input ${dataAttribute} type="${inputType}" value="${escapeHTML(textValue)}"${placeholder}${stringConstraints}${requiredAttribute} />`;
}

function arrayDefaultValues(definition: ParameterDefinition): unknown[] {
  if (!definition.defaultValue) return [];
  try {
    const parsed = JSON.parse(definition.defaultValue) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function arrayItemHTML(definition: ParameterDefinition, value: unknown = ""): string {
  return `<div class="runtime-array-item">${scalarControlHTML(definition.items!, "data-runtime-array-item", value, false, true)}<button type="button" data-runtime-array-remove aria-label="删除此项">删除</button></div>`;
}

function arrayFieldHTML(definition: ParameterDefinition): string {
  const values = arrayDefaultValues(definition);
  const initialCount = Math.max(1, definition.minItems || 0, values.length);
  while (values.length < initialCount) values.push("");
  const maxItems = definition.maxItems || 20;
  return `<div class="field runtime-field runtime-array-field" data-runtime-array="${escapeHTML(definition.name)}">
    <span>${escapeHTML(definition.label)}${definition.required ? " *" : ""}</span>
    <div class="runtime-array-items">${values.slice(0, maxItems).map(value => arrayItemHTML(definition, value)).join("")}</div>
    <button class="runtime-array-add" type="button" data-runtime-array-add${values.length >= maxItems ? " disabled" : ""}>＋ 添加一项</button>
    ${definition.helpText ? `<small>${escapeHTML(definition.helpText)}</small>` : ""}
  </div>`;
}

function renderRequestForm(product: Product): void {
  const definitions = parseParameterDefinitions(product.requestSchema);
  if (definitions.length === 0) {
    let template = product.requestTemplate || "{}";
    try {
      template = JSON.stringify(JSON.parse(template), null, 2);
    } catch {
      // Keep the stored template visible for legacy products.
    }
    requestFields.innerHTML = `<label class="field"><span>请求参数 · JSON</span><textarea data-runtime-legacy rows="7" spellcheck="false">${escapeHTML(template)}</textarea></label>`;
    return;
  }
  requestFields.innerHTML = definitions.map(item => {
    if (item.type === "array" && item.items) return arrayFieldHTML(item);
    const control = scalarControlHTML(item, `data-runtime-name="${escapeHTML(item.name)}"`, item.defaultValue || "", item.required);
    return `<label class="field runtime-field"><span>${escapeHTML(item.label)}${item.required ? " *" : ""}</span>${control}${item.helpText ? `<small>${escapeHTML(item.helpText)}</small>` : ""}</label>`;
  }).join("");
}

function scalarRuntimeValue(definition: ParameterItemDefinition, control: HTMLInputElement | HTMLSelectElement): unknown {
  const raw = control.value.trim();
  if (definition.type === "boolean") return raw === "true";
  if (definition.type === "number") return Number(raw);
  if (definition.type === "integer") return Number.parseInt(raw, 10);
  return raw;
}

async function collectRequestValues(product: Product): Promise<Record<string, unknown>> {
  const definitions = parseParameterDefinitions(product.requestSchema);
  const legacy = requestFields.querySelector<HTMLTextAreaElement>("[data-runtime-legacy]");
  if (legacy) return JSON.parse(legacy.value) as Record<string, unknown>;
  const values: Record<string, unknown> = {};
  for (const item of definitions) {
    if (item.type === "array" && item.items) {
      const container = requestFields.querySelector<HTMLElement>(`[data-runtime-array="${CSS.escape(item.name)}"]`)!;
      const controls = [...container.querySelectorAll<HTMLInputElement | HTMLSelectElement>("[data-runtime-array-item]")];
      const entries = controls
        .filter(control => control.value.trim() !== "")
        .map(control => scalarRuntimeValue(item.items!, control));
      const minItems = item.minItems || 0;
      const maxItems = item.maxItems || 20;
      if ((item.required || minItems > 0) && entries.length < Math.max(1, minItems)) {
        throw new Error(`${item.label} 至少需要 ${Math.max(1, minItems)} 项。`);
      }
      if (entries.length > maxItems) throw new Error(`${item.label} 最多允许 ${maxItems} 项。`);
      if (item.uniqueItems && new Set(entries.map(value => JSON.stringify(value))).size !== entries.length) {
        throw new Error(`${item.label} 不能包含重复项。`);
      }
      if (entries.length > 0) values[item.name] = entries;
      continue;
    }
    const control = requestFields.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[data-runtime-name="${CSS.escape(item.name)}"]`)!;
    if (item.type === "file") {
      const file = (control as HTMLInputElement).files?.[0];
      if (!file) {
        if (item.required) throw new Error(`${item.label} 为必填文件。`);
        continue;
      }
      const maxBytes = item.maxBytes || 8388608;
      if (file.size > maxBytes) throw new Error(`${item.label} 超过 ${maxBytes} 字节限制。`);
      values[item.name] = {
        name: file.name,
        contentType: file.type || "application/octet-stream",
        base64: await fileToBase64(file),
      };
      continue;
    }
    if (item.type === "boolean") {
      values[item.name] = (control as HTMLInputElement).checked;
      continue;
    }
    const raw = control.value.trim();
    if (!raw) {
      if (item.required) throw new Error(`${item.label} 为必填参数。`);
      continue;
    }
    if (item.type === "number") values[item.name] = Number(raw);
    else if (item.type === "integer") values[item.name] = Number.parseInt(raw, 10);
    else if (item.type === "json") values[item.name] = JSON.parse(raw);
    else values[item.name] = raw;
  }
  return values;
}

requestFields.addEventListener("click", event => {
  const target = event.target as HTMLElement;
  const container = target.closest<HTMLElement>("[data-runtime-array]");
  if (!container || !selected) return;
  const definition = parseParameterDefinitions(selected.requestSchema)
    .find(item => item.name === container.dataset.runtimeArray && item.type === "array");
  if (!definition?.items) return;
  const items = container.querySelector<HTMLElement>(".runtime-array-items")!;
  if (target.closest("[data-runtime-array-add]")) {
    if (items.children.length < (definition.maxItems || 20)) {
      items.insertAdjacentHTML("beforeend", arrayItemHTML(definition));
    }
  } else {
    const remove = target.closest<HTMLButtonElement>("[data-runtime-array-remove]");
    const row = remove?.closest<HTMLElement>(".runtime-array-item");
    if (row && items.children.length > 1) row.remove();
    else if (row) {
      const control = row.querySelector<HTMLInputElement | HTMLSelectElement>("[data-runtime-array-item]");
      if (control?.type === "checkbox") (control as HTMLInputElement).checked = false;
      else if (control) control.value = "";
    }
  }
  const add = container.querySelector<HTMLButtonElement>("[data-runtime-array-add]")!;
  add.disabled = items.children.length >= (definition.maxItems || 20);
});

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`读取文件 ${file.name} 失败。`));
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1] || "");
    reader.readAsDataURL(file);
  });
}

function renderResponseView(
  productName: string,
  definition: ResponseDisplayDefinition,
  result: unknown,
  container: HTMLElement,
): void {
  const modeLabel = {
    TEXT: "文本结果",
    KEY_VALUE: "重点字段",
    LIST: "分组列表",
    TABLE: "列表结果",
    JSON: "JSON 数据",
  }[definition.mode];
  const bodyHTML = configuredResponseBodyHTML(definition, result);
  const rawHTML = definition.mode === "JSON"
    ? ""
    : `<details class="response-raw"><summary>查看原始响应 JSON</summary><pre>${escapeHTML(JSON.stringify(result, null, 2))}</pre></details>`;
  container.innerHTML = `
    <article class="response-view">
      <header class="response-view-head">
        <div><span>BUSINESS RESULT</span><h3>${escapeHTML(productName)}</h3></div>
        <div class="response-view-badges"><b>返回成功</b><em>${modeLabel}</em></div>
      </header>
      <div class="response-body">${bodyHTML}</div>
      ${rawHTML}
    </article>`;
}

function renderServiceResponse(
  product: Product,
  result: unknown,
  container: HTMLElement = serviceResult,
): void {
  renderResponseView(
    product.name,
    parseResponseDefinition(product.responseSchema),
    result,
    container,
  );
}

function formatUSDC(atomic: string): string {
  const padded = atomic.padStart(7, "0");
  const whole = padded.slice(0, -6).replace(/^0+(?=\d)/, "");
  const fraction = padded.slice(-6).replace(/0+$/, "");
  return `${whole}${fraction ? `.${fraction}` : ""} USDC`;
}

function formatHoldingDuration(seconds: number): string {
  if (seconds % 86400 === 0) return `${seconds / 86400} 天`;
  if (seconds % 3600 === 0) return `${seconds / 3600} 小时`;
  if (seconds % 60 === 0) return `${seconds / 60} 分钟`;
  return `${seconds} 秒`;
}

function batchDepositCallCount(): bigint {
  const value = Number.parseInt(batchDepositCalls.value, 10);
  if (!Number.isInteger(value) || value < 3 || value > 1000) {
    throw new Error("充值调用次数必须是 3 到 1000 之间的整数。");
  }
  return BigInt(value);
}

function selectedWalletChannel(product = selected): WalletBatchChannel | undefined {
  if (!product || !accountToken || !accountWallet) return undefined;
  if (walletAddress && accountWallet.toLowerCase() !== walletAddress.toLowerCase()) {
    return undefined;
  }
  return accountChannels.find(channel =>
    channel.network === product.network &&
    channel.token.toLowerCase() === product.tokenAddress.toLowerCase() &&
    channel.receiver.toLowerCase() === product.receiverWallet.toLowerCase(),
  );
}

function updateBatchPaymentPanel(): void {
  if (!selected || selectedPaymentMode !== "BATCH") return;
  const callPrice = BigInt(selected.priceUsdcAtomic);
  const channel = selectedWalletChannel();
  const hasEnoughBalance = channel !== undefined &&
    BigInt(channel.availableAmount) >= callPrice;
  const refundPending = channel?.refundRequest !== undefined;
  batchTopup.classList.toggle("has-balance", hasEnoughBalance);
  batchTopup.classList.toggle("needs-recharge", !hasEnoughBalance);
  batchCallGate.classList.toggle("ready", hasEnoughBalance && !refundPending);
  batchCallGate.classList.toggle("blocked", !hasEnoughBalance || refundPending);
  batchRechargeButton.disabled = batchTopUpPending || refundPending;

  try {
    const amount = callPrice * batchDepositCallCount();
    batchDepositTotal.textContent = formatUSDC(amount.toString());
    batchDepositNote.textContent =
      `充值 ${batchDepositCallCount().toString()} 次额度；充值成功后不会自动调用接口。`;
    if (refundPending && channel) {
      batchChannelHeadline.textContent =
        `该通道正在 ${formatHoldingDuration(batchRefundHoldingSeconds)}退款等待期中`;
      batchChannelDetail.textContent =
        `预计退款 ${formatUSDC(channel.refundRequest!.requestedAmountAtomic)}，到期前不能继续充值。`;
      batchCallHeadline.textContent = channel.refundRequest!.ready
        ? "退款已到期，请先在管理中心提取"
        : "退款账期中，暂停余额调用";
      batchCallDetail.textContent =
        `可提取时间：${new Date(channel.refundRequest!.availableAt).toLocaleString("zh-CN")}。`;
    } else if (hasEnoughBalance && channel) {
      batchChannelHeadline.textContent = `当前余额 ${formatUSDC(channel.availableAmount)}`;
      batchChannelDetail.textContent =
        "这是当前钱包在本结算组的共享余额，可用于任一上架服务。";
      batchCallHeadline.textContent = "共享余额充足，可以调用";
      batchCallDetail.textContent =
        `本次只从通道扣除 ${formatUSDC(selected.priceUsdcAtomic)}，不会再次充值。`;
    } else if (channel) {
      batchChannelHeadline.textContent = "余额不足，请在这里先充值";
      batchChannelDetail.textContent =
        `当前可用 ${formatUSDC(channel.availableAmount)}，充值完成后再到右侧调用。`;
      batchCallHeadline.textContent = "余额不足，暂不能调用";
      batchCallDetail.textContent = `请先在左侧充值，至少需要 ${formatUSDC(selected.priceUsdcAtomic)}。`;
    } else if (accountToken && accountWallet) {
      batchChannelHeadline.textContent = "还没有共享充值通道";
      batchChannelDetail.textContent = "点击下方充值后创建当前钱包的共享通道。";
      batchCallHeadline.textContent = "还没有可用余额";
      batchCallDetail.textContent = "请先在左侧完成充值，充值和接口调用是两个独立操作。";
    } else {
      batchChannelHeadline.textContent = "钱包登录后充值";
      batchChannelDetail.textContent =
        "点击充值会先要求钱包签名登录，再签署 USDC 充值授权。";
      batchCallHeadline.textContent = "请先登录并在左侧充值";
      batchCallDetail.textContent = "右侧仅使用已充值余额，不会自动补充资金。";
    }
  } catch {
    batchDepositTotal.textContent = "请输入 3 到 1000 次";
    batchDepositNote.textContent = "请输入有效的充值调用次数。";
    batchRechargeButton.disabled = true;
  }
}

function shortAddress(address: string): string {
  return `${address.slice(0, 8)}…${address.slice(-6)}`;
}

function stateLabel(state: string): string {
  return {
    DELIVERED: "已交付",
    SETTLED: "已结算",
    BATCH_COMMITTED: "通道已扣费",
    SUCCEEDED: "调用成功",
    PENDING: "处理中",
    CREATED: "已创建",
    FAILED: "失败",
  }[state] || state;
}

function renderAccountResultMetadata(order?: WalletOrder): void {
  accountResultProduct.textContent = order?.productName || "正在读取服务信息";
  accountResultOrder.textContent = order?.id || "—";
  accountResultStatus.textContent = order ? stateLabel(order.status) : "—";
  accountResultPayment.textContent = order ? stateLabel(order.paymentState) : "—";
  accountResultService.textContent = order ? stateLabel(order.serviceState) : "—";
  accountResultPrice.textContent = order ? formatUSDC(order.priceUsdcAtomic) : "—";
  accountResultTime.textContent = order
    ? new Date(order.createdAt).toLocaleString("zh-CN")
    : "—";
}

function showWalletIdentity(address?: string): void {
  walletIdentity.hidden = !address;
  walletLabel.textContent = address ? shortAddress(address) : "—";
}

function setAccountStatus(message: string, error = false): void {
  accountStatus.hidden = false;
  accountStatus.className = `account-status ${error ? "error" : "success"}`;
  accountStatus.textContent = message;
}

function showAccountCenter(): void {
  accountLabel.textContent = "管理中心";
  showWalletIdentity(accountWallet);
  accountLogin.hidden = true;
  accountCenter.hidden = false;
  accountModal.classList.add("has-account");
}

function clearAccountSession(hideWalletIdentity = false): void {
  const connectedWallet = walletAddress || accountWallet;
  accountToken = "";
  accountWallet = "";
  accountOrders = [];
  accountChannels = [];
  accountFundEvents = [];
  accountSubscriptions = [];
  accountPage = 1;
  accountTotalPages = 0;
  sessionStorage.removeItem("bizx402-wallet-token");
  sessionStorage.removeItem("bizx402-wallet-address");
  accountLabel.textContent = "钱包登录";
  showWalletIdentity(walletIdentityAfterSessionClear(connectedWallet, hideWalletIdentity));
  accountLogin.hidden = false;
  accountCenter.hidden = true;
  accountModal.classList.remove("has-account");
  accountResult.hidden = true;
  renderAccountChannels();
  renderAccountFundEvents();
  renderAccountSubscriptions();
  updateBatchPaymentPanel();
  updateCallButton();
}

async function accountApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("authorization", `Bearer ${accountToken}`);
  if (options.body) headers.set("content-type", "application/json");
  const response = await fetch(`${apiBase}${path}`, {
    ...options,
    headers,
  });
  const body = await response.json().catch(() => ({})) as T & {
    error?: { message?: string };
  };
  if (!response.ok) {
    if (response.status === 401) clearAccountSession();
    throw new Error(body.error?.message || `API 返回 HTTP ${response.status}`);
  }
  return body;
}

function renderAccountOrders(page: WalletOrderPage): void {
  accountOrders = page.orders;
  accountPage = page.pagination.page;
  accountTotalPages = page.pagination.totalPages;
  document.querySelector("#account-wallet")!.textContent = accountWallet;
  document.querySelector("#account-total-orders")!.textContent = String(page.summary.totalOrders);
  document.querySelector("#account-settled-orders")!.textContent =
    String(page.summary.paidOrders ?? page.summary.settledOrders);
  document.querySelector("#account-total-spent")!.textContent =
    formatUSDC(page.summary.totalSpentAtomic).replace(" USDC", "");
  document.querySelector<HTMLElement>("#account-order-empty")!.hidden = page.orders.length !== 0;
  accountOrderRows.innerHTML = page.orders.map(order => `
    <tr>
      <td><strong>${escapeHTML(order.productName)}</strong><small>${escapeHTML(order.id)}</small></td>
      <td><strong>${escapeHTML(formatUSDC(order.priceUsdcAtomic))}</strong></td>
      <td><span>${escapeHTML(order.paymentState)}</span><small>${escapeHTML(order.serviceState)}${order.errorCode ? ` · ${escapeHTML(order.errorCode)}` : ""}</small></td>
      <td><code>${escapeHTML(new Date(order.createdAt).toLocaleString("zh-CN"))}</code></td>
      <td><button class="account-result-button" data-account-order="${escapeHTML(order.id)}" type="button">查看结果</button></td>
    </tr>
  `).join("");
  accountOrderRows.querySelectorAll<HTMLButtonElement>("[data-account-order]").forEach(button => {
    button.addEventListener("click", () => void loadAccountOrderResult(button.dataset.accountOrder!));
  });
  const totalPages = Math.max(1, page.pagination.totalPages);
  document.querySelector("#account-page-label")!.textContent =
    `第 ${page.pagination.page} / ${totalPages} 页 · 共 ${page.pagination.total} 笔`;
  (document.querySelector<HTMLButtonElement>("#account-prev-page")!).disabled =
    page.pagination.page <= 1;
  (document.querySelector<HTMLButtonElement>("#account-next-page")!).disabled =
    page.pagination.totalPages === 0 || page.pagination.page >= page.pagination.totalPages;
}

function renderAccountChannels(): void {
  document.querySelector<HTMLElement>("#account-channel-empty")!.hidden =
    accountChannels.length !== 0;
  accountChannelGrid.innerHTML = accountChannels.map(channel => {
    const refund = channel.refundRequest;
    const refundButtons = refund?.status === "PROCESSING"
      ? `<button type="button" disabled>退款处理中</button>`
      : refund
      ? `<button data-refund-execute="${escapeHTML(refund.id)}" data-refund-channel="${escapeHTML(channel.channelId)}" type="button"${refund.ready ? "" : " disabled"}>${refund.ready ? "提取可退余额" : `${formatHoldingDuration(batchRefundHoldingSeconds)}等待中`}</button>
         <button class="account-refund-cancel" data-refund-cancel="${escapeHTML(refund.id)}" type="button">取消退款申请</button>`
      : `<button data-refund-request="${escapeHTML(channel.channelId)}" type="button">申请退回余额</button>`;
    const refundNote = refund
      ? `<p class="account-refund-note">${refund.status === "PROCESSING"
        ? "链上退款正在执行，当前不能取消或继续使用该通道。"
        : refund.ready
        ? "退款账期已结束，可将实际未消费余额提回付款钱包。"
        : `预计 ${escapeHTML(new Date(refund.availableAt).toLocaleString("zh-CN"))} 后可提取；执行时按实际未消费余额退款。`}</p>`
      : "";
    return `<article class="account-channel-card">
      <header>
        <div><span>SHARED PREPAID CHANNEL</span><h3>共享 USDC 余额</h3></div>
        <strong>${escapeHTML(formatUSDC(channel.availableAmount).replace(" USDC", ""))}<small>USDC 可用</small></strong>
      </header>
      <div class="account-channel-balance">
        <span>可用余额</span>
        <b>${escapeHTML(formatUSDC(channel.availableAmount))}</b>
      </div>
      <dl>
        <div><dt>通道余额</dt><dd>${escapeHTML(formatUSDC(channel.balance))}</dd></div>
        <div><dt>累计消费</dt><dd>${escapeHTML(formatUSDC(channel.chargedCumulativeAmount))}</dd></div>
        <div><dt>已使用产品</dt><dd title="${escapeHTML((channel.productNames || [channel.productName]).join("、"))}">${escapeHTML((channel.productNames || [channel.productName]).join("、"))}</dd></div>
        <div><dt>通道编号</dt><dd title="${escapeHTML(channel.channelId)}">${escapeHTML(shortAddress(channel.channelId))}</dd></div>
      </dl>
      ${refundNote}
      <div class="account-channel-actions">
        <button data-channel-product="${escapeHTML(channel.productIds?.[0] || channel.productId)}" type="button"${refund ? " disabled" : ""}>选择服务调用</button>
        ${refundButtons}
      </div>
    </article>`;
  }).join("");
  accountChannelGrid.querySelectorAll<HTMLButtonElement>("[data-channel-product]").forEach(button => {
    button.addEventListener("click", () => {
      const product = products.find(item => item.id === button.dataset.channelProduct);
      if (!product) {
        setAccountStatus("该产品当前未上架，暂时不能继续调用。", true);
        return;
      }
      accountModal.close();
      selectProduct(product, true, "BATCH");
    });
  });
  accountChannelGrid.querySelectorAll<HTMLButtonElement>("[data-refund-request]").forEach(button => {
    button.addEventListener("click", () => void requestBatchRefund(
      button.dataset.refundRequest!,
      button,
    ));
  });
  accountChannelGrid.querySelectorAll<HTMLButtonElement>("[data-refund-execute]").forEach(button => {
    button.addEventListener("click", () => void executeBatchRefund(
      button.dataset.refundExecute!,
      button.dataset.refundChannel!,
      button,
    ));
  });
  accountChannelGrid.querySelectorAll<HTMLButtonElement>("[data-refund-cancel]").forEach(button => {
    button.addEventListener("click", () => void cancelBatchRefund(
      button.dataset.refundCancel!,
      button,
    ));
  });
}

function renderAccountFundEvents(): void {
  document.querySelector<HTMLElement>("#account-fund-empty")!.hidden =
    accountFundEvents.length !== 0;
  accountFundRows.innerHTML = accountFundEvents.map(event => {
    const amount = event.status === "COMPLETED" && event.actualAmountAtomic !== "0"
      ? event.actualAmountAtomic
      : event.requestedAmountAtomic;
    const status = event.kind === "REFUND" && event.status === "PENDING"
      ? event.ready ? "可提取" : `${formatHoldingDuration(batchRefundHoldingSeconds)}等待中`
      : event.status === "COMPLETED" ? "已完成"
      : event.status === "CANCELLED" ? "已取消"
      : event.status === "PROCESSING" ? "退款处理中"
      : event.status;
    const time = event.kind === "REFUND" && event.status === "PENDING" && event.availableAt
      ? `可提取 ${new Date(event.availableAt).toLocaleString("zh-CN")}`
      : new Date(event.createdAt).toLocaleString("zh-CN");
    return `<tr>
      <td><strong>${event.kind === "TOP_UP" ? "充值" : "退款"}</strong><small>${escapeHTML(event.productName)}</small></td>
      <td><strong>${escapeHTML(formatUSDC(amount))}</strong></td>
      <td><span>${escapeHTML(status)}</span><small>${event.transactionHash ? escapeHTML(shortAddress(event.transactionHash)) : escapeHTML(shortAddress(event.channelId))}</small></td>
      <td><code>${escapeHTML(time)}</code></td>
    </tr>`;
  }).join("");
}

function renderAccountSubscriptions(): void {
  document.querySelector<HTMLElement>("#account-subscription-empty")!.hidden =
    accountSubscriptions.length !== 0;
  accountSubscriptionRows.innerHTML = accountSubscriptions.map(subscription => {
    const callbackStatus = subscription.terminationCallbackStatus ||
      subscription.activationCallbackStatus || "—";
    const callbackLabel = subscription.terminationCallbackStatus
      ? `终止回调 ${stateLabel(callbackStatus)}`
      : subscription.activationCallbackStatus
      ? `开通回调 ${stateLabel(callbackStatus)}`
      : "暂无回调状态";
    const createdAt = subscription.activatedAt && !subscription.activatedAt.startsWith("0001-")
      ? subscription.activatedAt
      : subscription.createdAt;
    return `<tr>
      <td><strong>${escapeHTML(subscription.productName)}</strong><small>${escapeHTML(subscription.id)} · 订单 ${escapeHTML(subscription.orderId)}</small></td>
      <td><strong>${subscription.quotaRemaining} / ${subscription.quotaTotal} 次</strong><small>每笔购买独立计费，不与其他订阅合并</small></td>
      <td><strong>${escapeHTML(formatUSDC(subscription.balanceRemainingAtomic))}</strong><small>购买金额 ${escapeHTML(formatUSDC(subscription.balanceTotalAtomic))}</small></td>
      <td><span>${escapeHTML(stateLabel(subscription.status))}</span><small>${escapeHTML(callbackLabel)}${subscription.terminationReason ? ` · ${escapeHTML(stateLabel(subscription.terminationReason))}` : ""}</small></td>
      <td><code>${escapeHTML(new Date(createdAt).toLocaleString("zh-CN"))}</code></td>
    </tr>`;
  }).join("");
}

async function loadAccountOrders(page = 1): Promise<void> {
  if (!accountToken) return;
  try {
    const result = await accountApi<WalletOrderPage>(`/v1/me/orders?page=${page}&pageSize=10`);
    showAccountCenter();
    renderAccountOrders(result);
    accountStatus.hidden = true;
  } catch (error) {
    setAccountStatus(errorMessage(error), true);
  }
}

async function loadAccountChannels(): Promise<void> {
  if (!accountToken) return;
  try {
    const result = await accountApi<{ enabled: boolean; channels: WalletBatchChannel[] }>(
      "/v1/me/batch/channels",
    );
    await Promise.allSettled(result.channels.map(channel => batchChannelStorage.set(
      channel.channelId,
      {
        balance: channel.balance,
        chargedCumulativeAmount: channel.chargedCumulativeAmount,
        totalClaimed: channel.totalClaimed,
        signedMaxClaimable: channel.signedMaxClaimable,
        signature: channel.signature,
      },
    )));
    accountChannels = result.channels;
    renderAccountChannels();
    updateBatchPaymentPanel();
    updateCallButton();
  } catch (error) {
    accountChannels = [];
    renderAccountChannels();
    updateBatchPaymentPanel();
    updateCallButton();
    setAccountStatus(errorMessage(error), true);
  }
}

async function loadAccountFundEvents(): Promise<void> {
  if (!accountToken) return;
  try {
    const result = await accountApi<{
      refundHoldingSeconds: number;
      events: WalletBatchFundEvent[];
    }>(
      "/v1/me/batch/fund-events",
    );
    batchRefundHoldingSeconds = result.refundHoldingSeconds;
    accountFundEvents = result.events;
    renderAccountFundEvents();
  } catch (error) {
    accountFundEvents = [];
    renderAccountFundEvents();
    setAccountStatus(errorMessage(error), true);
  }
}

async function loadAccountSubscriptions(): Promise<void> {
  if (!accountToken) return;
  try {
    const result = await accountApi<{ subscriptions: WalletNotificationSubscription[] }>(
      "/v1/me/notification-subscriptions",
    );
    accountSubscriptions = result.subscriptions;
    renderAccountSubscriptions();
  } catch (error) {
    accountSubscriptions = [];
    renderAccountSubscriptions();
    setAccountStatus(errorMessage(error), true);
  }
}

async function requestBatchRefund(
  channelID: string,
  button: HTMLButtonElement,
): Promise<void> {
  if (batchRefundPending) return;
  batchRefundPending = true;
  const original = button.textContent;
  button.disabled = true;
  button.textContent = "提交中…";
  try {
    const result = await accountApi<{ refundRequest: WalletBatchFundEvent }>(
      `/v1/me/batch/channels/${encodeURIComponent(channelID)}/refund-requests`,
      { method: "POST", body: "{}" },
    );
    setAccountStatus(
      `退款申请已提交，预计 ${new Date(result.refundRequest.availableAt!).toLocaleString("zh-CN")} 后可提取。`,
    );
    await Promise.all([loadAccountChannels(), loadAccountFundEvents()]);
  } catch (error) {
    setAccountStatus(errorMessage(error), true);
    button.disabled = false;
    button.textContent = original;
  } finally {
    batchRefundPending = false;
  }
}

async function cancelBatchRefund(
  refundID: string,
  button: HTMLButtonElement,
): Promise<void> {
  if (batchRefundPending) return;
  if (!window.confirm("取消退款申请后，该通道会立即恢复充值和调用。确定取消吗？")) return;
  batchRefundPending = true;
  const original = button.textContent;
  button.disabled = true;
  button.textContent = "取消中…";
  try {
    await accountApi(
      `/v1/me/batch/refund-requests/${encodeURIComponent(refundID)}/cancel`,
      { method: "POST", body: "{}" },
    );
    setAccountStatus("退款申请已取消，通道余额可以继续使用。");
    await Promise.all([loadAccountChannels(), loadAccountFundEvents()]);
  } catch (error) {
    setAccountStatus(errorMessage(error), true);
    button.disabled = false;
    button.textContent = original;
  } finally {
    batchRefundPending = false;
  }
}

async function executeBatchRefund(
  refundID: string,
  channelID: string,
  button: HTMLButtonElement,
): Promise<void> {
  if (batchRefundPending) return;
  batchRefundPending = true;
  const original = button.textContent;
  button.disabled = true;
  button.textContent = "提取中…";
  try {
    const result = await accountApi<{
      refundRequest: WalletBatchFundEvent;
    }>(
      `/v1/me/batch/refund-requests/${encodeURIComponent(refundID)}/execute`,
      { method: "POST", body: "{}" },
    );
    await batchChannelStorage.delete(channelID);
    setAccountStatus(
      `退款已提交，实际退回 ${formatUSDC(result.refundRequest.actualAmountAtomic)}。`,
    );
    await Promise.all([loadAccountChannels(), loadAccountFundEvents()]);
  } catch (error) {
    setAccountStatus(errorMessage(error), true);
    button.disabled = false;
    button.textContent = original;
  } finally {
    batchRefundPending = false;
  }
}

async function loadAccountDashboard(page = 1): Promise<void> {
  await Promise.all([
    loadAccountOrders(page),
    loadAccountChannels(),
    loadAccountFundEvents(),
    loadAccountSubscriptions(),
  ]);
}

async function loadAccountOrderResult(orderID: string): Promise<void> {
  revealAccountResult(accountResult);
  renderAccountResultMetadata(accountOrders.find(order => order.id === orderID));
  accountResultContent.innerHTML = `
    <div class="result-loading" role="status">
      <span class="call-progress-spinner" aria-hidden="true"></span>
      <div><strong>正在读取历史结果</strong><p>正在加载该订单保存的接口返回数据。</p></div>
    </div>`;
  try {
    const body = await accountApi<{
      order: WalletOrder & { result?: unknown };
    }>(`/v1/me/orders/${encodeURIComponent(orderID)}`);
    renderAccountResultMetadata(body.order);
    if (body.order.result === undefined) {
      accountResultContent.innerHTML =
        `<p class="account-result-empty">该订单没有可展示的历史返回结果。升级前订单只保存了响应哈希。</p>`;
      return;
    }
    const wrapper = body.order.result as { result?: unknown };
    const result = wrapper && typeof wrapper === "object" && "result" in wrapper
      ? wrapper.result
      : body.order.result;
    const product = products.find(item => item.id === body.order.productId);
    if (product) {
      renderServiceResponse(product, result, accountResultContent);
    } else {
      renderResponseView(
        body.order.productName,
        { mode: "JSON", fields: [] },
        result,
        accountResultContent,
      );
    }
  } catch (error) {
    accountResultContent.innerHTML =
      `<p class="account-result-empty">${escapeHTML(errorMessage(error))}</p>`;
  }
}

function openAccountModal(): void {
  if (!accountModal.open) accountModal.showModal();
  document.body.classList.add("modal-open");
  if (accountToken) void loadAccountDashboard(accountPage);
}

function errorMessage(error: unknown): string {
  if (typeof error === "object" && error && "code" in error && error.code === 4001) {
    return "你取消了钱包操作，未发生付款。";
  }
  if (error instanceof Error && /user rejected|rejected the request/i.test(error.message)) {
    return "你取消了钱包操作，未发生付款。";
  }
  return error instanceof Error ? error.message : "调用失败，请重试。";
}

function isSupported(product: Product): boolean {
  return product.network === BASE_SEPOLIA_NETWORK &&
    product.tokenAddress.toLowerCase() === BASE_SEPOLIA_USDC.toLowerCase();
}

function hasConfiguredReceiver(product: Product): boolean {
  const receiver = product.receiverWallet.toLowerCase();
  return receiver !== "0x0000000000000000000000000000000000000000" &&
    receiver !== "0x0000000000000000000000000000000000000001";
}

function isPrepaidEntitlement(product: Product): boolean {
  return product.billingModel === "PREPAID_ENTITLEMENT";
}

function supportsPaymentMode(product: Product, mode: "EXACT" | "BATCH"): boolean {
  return product.supportedPaymentModes?.includes(mode) ?? product.paymentMode === mode;
}

function entitlementLabel(product: Product): string {
  const usageCount = product.notification?.includedUsageCount;
  return usageCount && usageCount > 0 ? `${usageCount} 次额度` : "服务额度";
}

function setStatus(message: string, kind: "normal" | "success" | "error" = "normal"): void {
  statusBox.hidden = false;
  statusBox.className = kind === "normal" ? "status" : `status ${kind}`;
  statusBox.textContent = message;
}

function renderCallProgress(): void {
  const elapsedSeconds = Math.max(
    0,
    Math.floor((performance.now() - callProgressStartedAt) / 1000),
  );
  const copy = callProgressCopy(callProgressStage, elapsedSeconds);
  callProgressTitle.textContent = copy.title;
  callProgressDetail.textContent = copy.detail;
  callProgressElapsed.textContent = `${elapsedSeconds} 秒`;
  callProgress.dataset.stage = callProgressStage;
  const activeIndex = callProgressStages.indexOf(callProgressStage);
  callProgress.querySelectorAll<HTMLElement>("[data-call-step]").forEach((step, index) => {
    step.classList.toggle("current", index === activeIndex);
    step.classList.toggle("complete", index < activeIndex);
  });
}

function setCallProgress(stage: CallProgressStage): void {
  callProgressStage = stage;
  renderCallProgress();
}

function startCallProgress(): void {
  callProgressStartedAt = performance.now();
  callProgress.hidden = false;
  requestCard.classList.add("is-processing");
  requestCard.setAttribute("aria-busy", "true");
  statusBox.hidden = true;
  setCallProgress("quote");
  callProgressTimer = window.setInterval(renderCallProgress, 250);
}

function stopCallProgress(): void {
  if (callProgressTimer !== undefined) window.clearInterval(callProgressTimer);
  callProgressTimer = undefined;
  callProgress.hidden = true;
  requestCard.classList.remove("is-processing");
  requestCard.removeAttribute("aria-busy");
}

function updateCallButton(): void {
  const isBatch = selectedPaymentMode === "BATCH";
  const channel = isBatch ? selectedWalletChannel() : undefined;
  const refundPending = channel?.refundRequest !== undefined;
  const hasBatchBalance = channel !== undefined && selected !== undefined &&
    BigInt(channel.availableAmount) >= BigInt(selected.priceUsdcAtomic);
  callButton.disabled = callPending || !selected || !isSupported(selected) ||
    !hasConfiguredReceiver(selected) ||
    Boolean(isBatch && (!hasBatchBalance || refundPending));
  const label = callButton.querySelector("span")!;
  if (callPending) {
    label.textContent = "正在处理，请稍候…";
  } else if (!selected) {
    label.textContent = "选择服务后调用";
  } else if (!isSupported(selected)) {
    label.textContent = "当前支付网络尚未开放";
  } else if (!hasConfiguredReceiver(selected)) {
    label.textContent = "请先在 Admin 设置收款钱包";
  } else {
    if (isBatch) {
      if (refundPending) {
        label.textContent = "退款账期中，暂停调用";
      } else if (hasBatchBalance && channel) {
        label.textContent = `使用通道余额 · 扣费 ${formatUSDC(selected.priceUsdcAtomic)}`;
      } else {
        label.textContent = "请先在左侧充值";
      }
    } else {
      label.textContent = isPrepaidEntitlement(selected)
        ? `支付 ${formatUSDC(selected.priceUsdcAtomic)} 并购买 ${entitlementLabel(selected)}`
        : `支付 ${formatUSDC(selected.priceUsdcAtomic)} 并调用`;
    }
  }
}

function responseModeLabel(mode: ResponseDisplayDefinition["mode"]): string {
  return {
    JSON: "完整 JSON",
    KEY_VALUE: "键值卡片",
    LIST: "数组分组列表",
    TABLE: "数据表格",
    TEXT: "文本结果",
  }[mode];
}

function openServiceModal(): void {
  if (!serviceModal.open) serviceModal.showModal();
  document.body.classList.add("modal-open");
  requestAnimationFrame(() => serviceModal.classList.add("is-visible"));
}

function closeServiceModal(updateURL = true): void {
  if (!serviceModal.open) return;
  if (updateURL && selectedProductSlug()) updateProductURL("", "replace");
  document.title = "x402 Service Portal";
  serviceModal.classList.remove("is-visible");
  window.setTimeout(() => {
    if (serviceModal.open && !serviceModal.classList.contains("is-visible")) {
      serviceModal.close();
    }
  }, 160);
}

function selectedProductSlug(): string {
  return new URL(window.location.href).searchParams.get("product") || "";
}

function selectedPaymentModeFromURL(): "EXACT" | "BATCH" {
  return new URL(window.location.href).searchParams.get("pay") === "batch"
    ? "BATCH"
    : "EXACT";
}

function updateProductURL(
  slug: string,
  mode: "push" | "replace",
  paymentMode: "EXACT" | "BATCH" = selectedPaymentMode,
): void {
  const url = new URL(window.location.href);
  if (slug) {
    url.searchParams.set("product", slug);
    url.searchParams.set("pay", paymentMode === "BATCH" ? "batch" : "exact");
  } else {
    url.searchParams.delete("product");
    url.searchParams.delete("pay");
  }
  window.history[mode === "push" ? "pushState" : "replaceState"](
    { product: slug || undefined },
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}

function selectProduct(
  product: Product,
  updateURL = true,
  paymentMode: "EXACT" | "BATCH" = "EXACT",
): void {
  selected = product;
  selectedPaymentMode = paymentMode;
  document.querySelectorAll(".product-card").forEach(card => {
    card.classList.toggle("selected", card.getAttribute("data-id") === product.id);
  });
  const responseDefinition = parseResponseDefinition(product.responseSchema);
  const isBatch = selectedPaymentMode === "BATCH";
  const isPrepaid = isPrepaidEntitlement(product);
  document.querySelector("#selected-capability")!.textContent =
    `${isBatch ? "BATCH 充值调用" : isPrepaid ? `EXACT 购买 ${entitlementLabel(product)}` : "EXACT 单次调用"} · ${product.providerType}${product.providerAction ? ` · ${product.providerAction}` : ""}`;
  document.querySelector("#checkout-title")!.textContent = product.name;
  document.querySelector("#selected-description")!.textContent = product.description;
  document.querySelector("#selected-method")!.textContent = `${product.method} · ${product.requestEncoding}`;
  document.querySelector("#selected-network")!.textContent =
    product.network === BASE_SEPOLIA_NETWORK ? "Base Sepolia" : product.network;
  document.querySelector("#selected-price")!.textContent = formatUSDC(product.priceUsdcAtomic);
  checkout.classList.toggle("batch-checkout", isBatch);
  batchTopup.hidden = !isBatch;
  batchCallGate.hidden = !isBatch;
  batchRechargeStatus.hidden = true;
  paymentSafetyNote.textContent = isBatch
    ? "BASE SEPOLIA · 共享 USDC 通道 · Claim + Settle 由管理员人工触发"
    : isPrepaid
      ? "BASE SEPOLIA · 套餐资金先进入平台 · 成功用量后逐次结算给服务商"
      : "BASE SEPOLIA · 测试 USDC · 平台代付链上 Gas";
  updateBatchPaymentPanel();
  document.querySelector("#selected-response-mode")!.textContent = responseModeLabel(responseDefinition.mode);
  document.querySelector("#selected-response-fields")!.textContent =
    [...(responseDefinition.summaryFields || []), ...(responseDefinition.fields || [])].length
      ? [...(responseDefinition.summaryFields || []), ...(responseDefinition.fields || [])]
        .map(field => field.label).join(" · ")
      : responseDefinition.mode === "JSON" ? "展示接口返回的完整 JSON 数据。" : "后台尚未配置返回展示字段。";
  renderRequestForm(product);
  checkout.hidden = false;
  orderResult.hidden = true;
  statusBox.hidden = true;
  updateCallButton();
  serviceModalPanel.scrollTop = 0;
  document.title = `${product.name} · x402 Service Portal`;
  if (updateURL) updateProductURL(product.slug, "push", paymentMode);
  openServiceModal();
}

function renderProducts(): void {
  if (products.length === 0) {
    productGrid.innerHTML = `<div class="empty-state"><strong>暂无已上架服务</strong><p>请先在 Admin 创建产品并上架。</p></div>`;
    catalogNote.textContent = "0 个可购买产品";
    return;
  }
  catalogNote.textContent = `${products.length} 个已上架服务 · 单次服务、额度套餐或通道充值`;
  productGrid.innerHTML = products.map((product, index) => `
    <article class="product-card${isSupported(product) ? "" : " unavailable"}" data-id="${escapeHTML(product.id)}">
      <div class="product-top"><span>${escapeHTML(product.providerType || "API")} / ${String(index + 1).padStart(2, "0")}</span><span>${isPrepaidEntitlement(product) ? "EXACT · 预付额度" : "EXACT + BATCH"} · V${product.version}</span></div>
      <h3>${escapeHTML(product.name)}</h3>
      <p>${escapeHTML(product.description)}</p>
      <span class="network-tag">${escapeHTML(product.network === BASE_SEPOLIA_NETWORK ? "BASE SEPOLIA" : product.network)}</span>
      <div class="product-price"><strong>${escapeHTML(formatUSDC(product.priceUsdcAtomic).replace(" USDC", ""))}</strong><span>${isPrepaidEntitlement(product) ? `USDC / ${escapeHTML(entitlementLabel(product))}` : "USDC / CALL"}</span></div>
      <dl class="product-facts">
        <div><dt>METHOD</dt><dd>${escapeHTML(product.method)}</dd></div>
        <div><dt>RECEIVER</dt><dd title="${escapeHTML(product.receiverWallet)}">${escapeHTML(shortAddress(product.receiverWallet))}</dd></div>
      </dl>
      <div class="product-actions">
        <button class="choose-button" data-payment-mode="EXACT" type="button"><span>${!isSupported(product) ? "暂未开放" : hasConfiguredReceiver(product) ? isPrepaidEntitlement(product) ? `购买 ${escapeHTML(entitlementLabel(product))}` : "单次调用" : "配置收款地址"}</span><b>→</b></button>
        ${supportsPaymentMode(product, "BATCH") ? `<button class="choose-button batch-choice" data-payment-mode="BATCH" type="button"><span>${!isSupported(product) ? "暂未开放" : hasConfiguredReceiver(product) ? "充值调用" : "配置收款地址"}</span><b>→</b></button>` : ""}
      </div>
    </article>
  `).join("");
  productGrid.querySelectorAll<HTMLElement>(".product-card").forEach(card => {
    card.querySelectorAll<HTMLButtonElement>("[data-payment-mode]").forEach(button => {
      button.addEventListener("click", () => {
        const product = products.find(item => item.id === card.dataset.id);
        if (product) selectProduct(
          product,
          true,
          button.dataset.paymentMode === "BATCH" ? "BATCH" : "EXACT",
        );
      });
    });
  });
}

async function loadProducts(): Promise<void> {
  try {
    const response = await fetch(`${apiBase}/v1/products`);
    if (!response.ok) throw new Error(`API 返回 ${response.status}`);
    const body = await response.json() as { products: Product[] };
    products = body.products;
    renderProducts();
    const linkedProduct = products.find(product => product.slug === selectedProductSlug());
    if (linkedProduct) selectProduct(linkedProduct, false, selectedPaymentModeFromURL());
  } catch (error) {
    catalogNote.textContent = "控制面离线";
    productGrid.innerHTML = `<div class="empty-state error"><strong>读取服务失败</strong><p>${escapeHTML(errorMessage(error))}</p></div>`;
  }
}

async function switchToBaseSepolia(activeProvider: EIP1193Provider): Promise<void> {
  const chainId = "0x14a34";
  const currentChain = await activeProvider.request({ method: "eth_chainId" });
  if (currentChain === chainId) return;
  try {
    await activeProvider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId }],
    });
  } catch (error) {
    if (!(typeof error === "object" && error && "code" in error && error.code === 4902)) {
      throw error;
    }
    await activeProvider.request({
      method: "wallet_addEthereumChain",
      params: [{
        chainId,
        chainName: "Base Sepolia",
        nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
        rpcUrls: [BASE_SEPOLIA_RPC],
        blockExplorerUrls: ["https://sepolia.basescan.org"],
      }],
    });
  }
}

async function requestWalletAccount(switchNetwork: boolean): Promise<`0x${string}`> {
  provider = window.ethereum;
  if (!provider) {
    throw new Error("未检测到浏览器钱包，请安装 MetaMask、Coinbase Wallet 或 Rabby。");
  }
  if (switchNetwork) await switchToBaseSepolia(provider);
  const accounts = await provider.request({ method: "eth_requestAccounts" }) as string[];
  if (!accounts[0]) throw new Error("钱包没有返回可用账户");
  walletAddress = accounts[0] as `0x${string}`;
  showWalletIdentity(walletAddress);
  return walletAddress;
}

async function connectWallet(): Promise<boolean> {
  try {
    await requestWalletAccount(true);
    updateBatchPaymentPanel();
    updateCallButton();
    setStatus("钱包已连接到 Base Sepolia。", "success");
    return true;
  } catch (error) {
    setStatus(errorMessage(error), "error");
    return false;
  }
}

async function signInWithWallet(): Promise<boolean> {
  const signInButton = document.querySelector<HTMLButtonElement>("#wallet-sign-in")!;
  signInButton.disabled = true;
  accountButton.disabled = true;
  setAccountStatus("正在连接钱包…");
  try {
    const wallet = await requestWalletAccount(false);
    const challengeResponse = await fetch(`${apiBase}/v1/auth/wallet/challenge`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ walletAddress: wallet }),
    });
    if (!challengeResponse.ok) throw new Error(await readError(challengeResponse));
    const challengeBody = await challengeResponse.json() as {
      challenge: { nonce: string; message: string; expiresAt: string };
    };
    setAccountStatus("请在钱包中签署登录消息，不会产生 Gas。");
    const signature = await (provider as unknown as {
      request: (args: { method: string; params: unknown[] }) => Promise<unknown>;
    }).request({
      method: "personal_sign",
      params: [challengeBody.challenge.message, wallet],
    }) as string;
    const verifyResponse = await fetch(`${apiBase}/v1/auth/wallet/verify`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        walletAddress: wallet,
        nonce: challengeBody.challenge.nonce,
        signature,
      }),
    });
    if (!verifyResponse.ok) throw new Error(await readError(verifyResponse));
    const body = await verifyResponse.json() as {
      token: string;
      walletAddress: string;
    };
    accountToken = body.token;
    accountWallet = body.walletAddress;
    sessionStorage.setItem("bizx402-wallet-token", accountToken);
    sessionStorage.setItem("bizx402-wallet-address", accountWallet);
    showAccountCenter();
    setAccountStatus("钱包登录成功。");
    await loadAccountDashboard(1);
    return true;
  } catch (error) {
    setAccountStatus(errorMessage(error), true);
    return false;
  } finally {
    signInButton.disabled = false;
    accountButton.disabled = false;
  }
}

async function readError(response: Response): Promise<string> {
  try {
    const body = await response.clone().json() as {
      error?: string | { message?: string };
      message?: string;
    };
    if (typeof body.error === "string") return body.error;
    if (body.error?.message) return body.error.message;
    if (body.message) return body.message;
  } catch {
    // x402 payment verification failures may intentionally return an empty body.
  }
  if (response.status === 402) {
    const paymentFailure = paymentFailureMessageFromHeader(
      response.headers.get("payment-required"),
    );
    if (paymentFailure) return paymentFailure;
  }
  return `API 返回 HTTP ${response.status}`;
}

function setBatchRechargeStatus(message: string, error = false): void {
  batchRechargeStatus.hidden = false;
  batchRechargeStatus.className = `batch-recharge-status${error ? " error" : ""}`;
  batchRechargeStatus.textContent = message;
}

async function executeBatchTopUp(): Promise<void> {
  if (!selected || selectedPaymentMode !== "BATCH" || batchTopUpPending) return;
  let depositAmount: bigint;
  try {
    depositAmount = BigInt(selected.priceUsdcAtomic) * batchDepositCallCount();
  } catch (error) {
    setBatchRechargeStatus(errorMessage(error), true);
    return;
  }
  if (!accountToken || !accountWallet) {
    setBatchRechargeStatus("请先完成钱包签名登录…");
    if (!await signInWithWallet()) return;
  }
  try {
    const connectedWallet = await requestWalletAccount(true);
    if (!accountWallet || accountWallet.toLowerCase() !== connectedWallet.toLowerCase()) {
      clearAccountSession();
      setBatchRechargeStatus("钱包账户已切换，请重新签名登录…");
      if (!await signInWithWallet()) return;
    }
  } catch (error) {
    setBatchRechargeStatus(errorMessage(error), true);
    return;
  }
  if (!provider || !walletAddress) return;
  const activeProduct = selected;
  batchTopUpPending = true;
  batchRechargeButton.disabled = true;
  batchRechargeButton.querySelector("span")!.textContent = "正在准备充值…";
  setBatchRechargeStatus("正在获取充值要求，请在钱包中完成两次签名授权。");
  try {
    await switchToBaseSepolia(provider);
    const signer = createVerifiedBrowserWalletSigner(provider, walletAddress);
    const publicClient = createPublicClient({
      chain: baseSepolia,
      transport: http(BASE_SEPOLIA_RPC),
    });
    const clientSigner = toClientEvmSigner(signer, publicClient);
    const clientRef = crypto.randomUUID();
    const body = JSON.stringify({ amountAtomic: depositAmount.toString() });
    const topUpURL = `${apiBase}${activeProduct.callPaths?.batchTopUp ||
      `/v1/batch/products/${encodeURIComponent(activeProduct.id)}/top-up`}`;
    const probe = await fetch(topUpURL, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-client-request-id": clientRef,
      },
      body,
    });
    if (probe.status !== 402) {
      throw new Error(probe.ok ? "充值接口未返回 x402 付款要求" : await readError(probe));
    }
    const requiredHeader = probe.headers.get("payment-required");
    assertExpectedPaymentChallenge(requiredHeader, {
      scheme: "batch-settlement",
      network: activeProduct.network,
      tokenAddress: activeProduct.tokenAddress,
      amount: "0",
      payTo: activeProduct.receiverWallet,
    });
    if (!requiredHeader) throw new Error("充值响应缺少 PAYMENT-REQUIRED");
    const paymentRequired = decodePaymentRequiredHeader(requiredHeader);
    const accepted = paymentRequired.accepts.find(item =>
      item.scheme === "batch-settlement" &&
      item.network === activeProduct.network &&
      item.asset.toLowerCase() === activeProduct.tokenAddress.toLowerCase(),
    ) as PaymentRequirements | undefined;
    if (!accepted) throw new Error("充值付款要求与当前产品不匹配");
    const existingChannel = selectedWalletChannel(activeProduct);
    const salt = batchChannelSalt(existingChannel);
    const deps = {
      signer: clientSigner,
      storage: batchChannelStorage,
      salt,
    };
    const channelConfig = buildChannelConfig(deps, accepted);
    assertBatchChannelIdentity(existingChannel, channelConfig, accepted.network);
    const channelID = computeChannelId(channelConfig, accepted.network);
    const current = await batchChannelStorage.get(channelID);
    const [, onchainTotalClaimed] = await readChannelBalanceAndTotalClaimed(
      clientSigner,
      channelID,
    );
    const maxClaimableAmount = batchTopUpCumulativeAmount(
      onchainTotalClaimed.toString(),
      current,
      existingChannel,
    );
    const payloadResult = await createBatchSettlementEIP3009DepositPayload(
      clientSigner,
      paymentRequired.x402Version,
      accepted,
      channelConfig,
      depositAmount.toString(),
      maxClaimableAmount,
    );
    const paymentPayload: PaymentPayload = {
      ...payloadResult,
      accepted,
      resource: paymentRequired.resource,
      extensions: paymentRequired.extensions,
    };
    batchRechargeButton.querySelector("span")!.textContent = "正在提交充值…";
    setBatchRechargeStatus("充值授权已签署，正在由 Facilitator 提交链上充值。");
    const response = await fetch(topUpURL, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-client-request-id": clientRef,
        "payment-signature": encodePaymentSignatureHeader(paymentPayload),
      },
      body,
    });
    if (!response.ok) throw new Error(await readError(response));
    const paymentResponse = response.headers.get("payment-response");
    if (!paymentResponse) throw new Error("充值响应缺少 PAYMENT-RESPONSE");
    const settlement = decodePaymentResponseHeader(paymentResponse);
    if (!settlement.success) throw new Error("充值没有成功结算");
    await processSettleResponse(batchChannelStorage, settlement);
    setBatchRechargeStatus(
      `充值完成：${formatUSDC(depositAmount.toString())} 已进入共享通道。`,
    );
    await loadAccountDashboard(1);
  } catch (error) {
    setBatchRechargeStatus(errorMessage(error), true);
  } finally {
    batchTopUpPending = false;
    batchRechargeButton.querySelector("span")!.textContent = "向共享通道充值";
    updateBatchPaymentPanel();
    updateCallButton();
  }
}

async function executePaidCall(): Promise<void> {
  if (!selected || !isSupported(selected) || !hasConfiguredReceiver(selected)) return;
  if (selectedPaymentMode === "BATCH") {
    const channel = selectedWalletChannel();
    if (channel?.refundRequest) {
      setStatus("该通道正在退款账期中，请先在管理中心完成退款。", "error");
      return;
    }
    if (!channel || BigInt(channel.availableAmount) < BigInt(selected.priceUsdcAtomic)) {
      setStatus("通道余额不足，请先在左侧完成独立充值。", "error");
      return;
    }
  }
  let requestBody: unknown;
  try {
    requestBody = await collectRequestValues(selected);
  } catch (error) {
    setStatus(errorMessage(error), "error");
    return;
  }
  try {
    await requestWalletAccount(true);
  } catch (error) {
    setStatus(errorMessage(error), "error");
    return;
  }
  if (!provider || !walletAddress) return;

  callPending = true;
  startCallProgress();
  updateCallButton();
  orderResult.hidden = true;
  try {
    await switchToBaseSepolia(provider);
    const signer = createVerifiedBrowserWalletSigner(provider, walletAddress);
    const isBatch = selectedPaymentMode === "BATCH";
    const client = new x402Client();
    const activeBatchChannel = isBatch ? selectedWalletChannel() : undefined;
    const activeBatchSalt = isBatch ? batchChannelSalt(activeBatchChannel) : undefined;
    let batchClientSigner: ReturnType<typeof toClientEvmSigner> | undefined;
    if (isBatch) {
      const publicClient = createPublicClient({
        chain: baseSepolia,
        transport: http(BASE_SEPOLIA_RPC),
      });
      batchClientSigner = toClientEvmSigner(signer, publicClient);
      client.register(
        "eip155:*",
        new BatchSettlementEvmScheme(
          batchClientSigner,
          {
            salt: activeBatchSalt!,
            rpcUrl: BASE_SEPOLIA_RPC,
            storage: batchChannelStorage,
            depositStrategy: () => false,
          },
        ),
      );
    } else {
      client.register(
        BASE_SEPOLIA_NETWORK,
        new ExactEvmScheme(signer, { rpcUrl: BASE_SEPOLIA_RPC }),
      );
    }
    let requestAttempt = 0;
    const guardedFetch: typeof fetch = async (input, init) => {
      requestAttempt += 1;
      if (requestAttempt > 1) setCallProgress("service");
      const response = await fetch(input, init);
      if (response.status === 402) {
        const requiredHeader = response.headers.get("payment-required");
        assertExpectedPaymentChallenge(requiredHeader, {
          scheme: isBatch ? "batch-settlement" : "exact",
          network: selected!.network,
          tokenAddress: selected!.tokenAddress,
          amount: selected!.priceUsdcAtomic,
          payTo: selected!.receiverWallet,
        });
        if (isBatch && activeBatchChannel && activeBatchSalt && batchClientSigner) {
          if (!requiredHeader) throw new Error("服务响应缺少 PAYMENT-REQUIRED");
          const paymentRequired = decodePaymentRequiredHeader(requiredHeader);
          const accepted = paymentRequired.accepts.find(item =>
            item.scheme === "batch-settlement" &&
            item.network === selected!.network &&
            item.asset.toLowerCase() === selected!.tokenAddress.toLowerCase(),
          ) as PaymentRequirements | undefined;
          if (!accepted) throw new Error("通道付款要求与当前产品不匹配");
          const channelConfig = buildChannelConfig(
            {
              signer: batchClientSigner,
              storage: batchChannelStorage,
              salt: activeBatchSalt,
            },
            accepted,
          );
          assertBatchChannelIdentity(activeBatchChannel, channelConfig, accepted.network);
        }
        setCallProgress("signature");
      }
      return response;
    };

    const requestID = crypto.randomUUID();
    const response = await wrapFetchWithPayment(guardedFetch, client)(
      `${apiBase}${isBatch
        ? selected.callPaths?.batch || `/v1/batch/products/${selected.id}/call`
        : selected.callPaths?.exact || `/v1/products/${selected.id}/call`}`,
      {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "x-client-request-id": requestID,
        },
        body: JSON.stringify(requestBody),
      },
    );
    if (!response.ok) throw new Error(await readError(response));
    setCallProgress("result");
    const body = await response.json() as unknown;
    const paymentHeader = response.headers.get("payment-response");
    if (!paymentHeader) throw new Error("服务响应缺少 PAYMENT-RESPONSE");
    const settlement = decodePaymentResponseHeader(paymentHeader);
    if (!settlement.success || (!isBatch && !settlement.transaction)) {
      throw new Error(isBatch ? "通道扣费没有成功提交" : "付款没有成功结算");
    }

    const callResult = normalizePaidCallResponse(body, selected.name);
    const orderID = response.headers.get("x-order-id") || callResult.orderId;
    const orderResponse = await fetch(`${apiBase}/v1/orders/${encodeURIComponent(orderID)}`);
    const orderBody = orderResponse.ok
      ? await orderResponse.json() as { order: Order }
      : { order: { id: orderID, status: "DELIVERED", paymentState: "SETTLED", serviceState: "SUCCEEDED" } };
    const order = orderBody.order;
    document.querySelector("#order-id")!.textContent = order.id;
    document.querySelector("#result-product")!.textContent = callResult.productName;
    document.querySelector("#result-summary")!.textContent =
      `${callResult.productName} 已完成，本次返回结果已经保存到订单记录。`;
    document.querySelector("#result-status")!.textContent = stateLabel(order.status);
    document.querySelector("#result-payment")!.textContent = stateLabel(order.paymentState);
    document.querySelector("#result-service")!.textContent =
      `${stateLabel(order.serviceState)} · HTTP ${callResult.serviceStatus}`;
    document.querySelector("#result-time")!.textContent =
      new Date(callResult.calledAt).toLocaleString("zh-CN");
    renderServiceResponse(selected, callResult.result);
    checkout.hidden = true;
    orderResult.hidden = false;
    setStatus(
      isBatch
        ? `通道已扣费，目标服务返回 HTTP ${callResult.serviceStatus}。`
        : isPrepaidEntitlement(selected)
          ? `${entitlementLabel(selected)} 已购买，服务已开通。`
          : `支付已结算，目标服务返回 HTTP ${callResult.serviceStatus}。`,
      "success",
    );
    serviceModalPanel.scrollTop = 0;
    if (accountToken && accountWallet && walletAddress &&
      accountWallet.toLowerCase() === walletAddress.toLowerCase()) {
      void loadAccountDashboard(1);
    } else {
      updateBatchPaymentPanel();
    }
  } catch (error) {
    setStatus(errorMessage(error), "error");
  } finally {
    callPending = false;
    stopCallProgress();
    updateCallButton();
  }
}

accountButton.addEventListener("click", () => {
  openAccountModal();
  if (!accountToken) void signInWithWallet();
});
document.querySelector("#wallet-sign-in")!.addEventListener("click", () => void signInWithWallet());
document.querySelector("#wallet-sign-out")!.addEventListener("click", () => {
  clearAccountSession(true);
  setAccountStatus("已退出钱包登录。");
});
document.querySelector("#close-account-modal")!.addEventListener("click", () => accountModal.close());
document.querySelector("#close-account-result")!.addEventListener("click", () => {
  accountResult.hidden = true;
});
accountResult.addEventListener("click", event => {
  if (event.target === accountResult) accountResult.hidden = true;
});
document.querySelector("#account-prev-page")!.addEventListener("click", () => {
  if (accountPage > 1) void loadAccountOrders(accountPage - 1);
});
document.querySelector("#account-next-page")!.addEventListener("click", () => {
  if (accountPage < accountTotalPages) void loadAccountOrders(accountPage + 1);
});
accountModal.addEventListener("cancel", event => {
  event.preventDefault();
  if (!accountResult.hidden) accountResult.hidden = true;
  else accountModal.close();
});
accountModal.addEventListener("close", () => document.body.classList.remove("modal-open"));
accountModal.addEventListener("click", event => {
  if (event.target === accountModal) accountModal.close();
});
closeServiceModalButton.addEventListener("click", () => closeServiceModal());
serviceModal.addEventListener("cancel", event => {
  event.preventDefault();
  closeServiceModal();
});
serviceModal.addEventListener("close", () => document.body.classList.remove("modal-open"));
serviceModal.addEventListener("click", event => {
  if (event.target === serviceModal) closeServiceModal();
});
window.addEventListener("popstate", () => {
  const linkedProduct = products.find(product => product.slug === selectedProductSlug());
  if (linkedProduct) selectProduct(linkedProduct, false, selectedPaymentModeFromURL());
  else closeServiceModal(false);
});
orderForm.addEventListener("submit", event => {
  event.preventDefault();
  void executePaidCall();
});
batchDepositCalls.addEventListener("input", updateBatchPaymentPanel);
batchRechargeButton.addEventListener("click", () => void executeBatchTopUp());

updateCallButton();
if (accountToken && accountWallet) {
  showAccountCenter();
  void loadAccountChannels();
} else {
  clearAccountSession();
}
const eventProvider = window.ethereum as (EIP1193Provider & {
  on?: (event: string, listener: (accounts: string[]) => void) => void;
}) | undefined;
eventProvider?.on?.("accountsChanged", accounts => {
  const next = accounts[0] as `0x${string}` | undefined;
  walletAddress = next;
  showWalletIdentity(next);
  updateBatchPaymentPanel();
  updateCallButton();
  if (accountWallet && (!next || accountWallet.toLowerCase() !== next.toLowerCase())) {
    clearAccountSession();
    setAccountStatus("钱包账户已切换，请重新签名登录。", true);
  }
});
if (!isServiceCLIRoute) void loadProducts();
